'use client';

import { FileText, FileSpreadsheet, Presentation, Mail, Loader2, Download } from 'lucide-react';
import { Card } from '../atomic/Card';
import { Button } from '../atomic/Button';
import { useState } from 'react';
import { generatePDFReport, generateInfographic } from '@/services/export';
import { analytics } from '@/utils/analytics';

interface ExportSectionProps {
  gemeindeName: string;
  gemeindeId?: string;
  data: {
    jahresPotenzial: number;
    einwohner: number;
    haushalte: number;
    energieQuellen: Array<{
      name: string;
      anzahl: number;
      kwh: number;
      potentialEuro: number;
    }>;
  };
}

export function ExportSection({ gemeindeName, gemeindeId, data }: ExportSectionProps) {
  const [isExporting, setIsExporting] = useState<string | null>(null);

  const handlePDFExport = async () => {
    setIsExporting('pdf');
    analytics.trackPdfExport(gemeindeName, 'full_report');
    try {
      await generatePDFReport(gemeindeName, data);
      analytics.track('pdf_export_success', { 
        municipality: gemeindeName,
        type: 'full_report'
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      analytics.trackError(error as Error, 'pdf_export');
      alert('Fehler beim Erstellen des PDF-Berichts');
    } finally {
      setIsExporting(null);
    }
  };

  const handleExcelExport = async () => {
    setIsExporting('excel');
    analytics.track('csv_export', { municipality: gemeindeName });
    try {
      // Create detailed CSV data
      const csvContent = [
        ['ENERGIEGEMEINSCHAFT POTENZIALANALYSE'],
        [`Gemeinde: ${gemeindeName}`],
        [`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`],
        [],
        ['ZUSAMMENFASSUNG'],
        ['Einwohner', data.einwohner],
        ['Haushalte', data.haushalte],
        ['Jährliches Potenzial (€)', data.jahresPotenzial],
        [`Potenzial pro Einwohner (€)`, Math.round(data.jahresPotenzial / data.einwohner)],
        [`Potenzial pro Haushalt (€)`, Math.round(data.jahresPotenzial / data.haushalte)],
        [],
        ['ENERGIEQUELLEN'],
        ['Energieträger', 'Anzahl Anlagen', 'Produktion (kWh)', 'Potenzial (€)', 'Anteil (%)'],
        ...data.energieQuellen.map(source => [
          source.name,
          source.anzahl,
          source.kwh,
          source.potentialEuro,
          Math.round((source.potentialEuro / data.jahresPotenzial) * 100)
        ]),
        [],
        ['BERECHNUNGSGRUNDLAGE'],
        ['Formel', 'Potenzial = Gesamtproduktion × 40% (handelbar) × 0,106 €/kWh'],
        ['Handelbare Energie', '40% der produzierten Energie'],
        ['Marktpreis', '0,106 €/kWh für regional erzeugte erneuerbare Energie'],
        [],
        ['BÜRGERBETEILIGUNG (Durchschnittswerte)'],
        ['Kategorie', 'Ersparnis pro Jahr (€)'],
        ['Durchschnittlicher Haushalt', '147'],
        ['Haushalt mit eigener PV-Anlage', '384'],
        ['Gewerbebetrieb', '1.250']
      ].map(row => row.join(';')).join('\n');

      // Download CSV with German formatting (semicolon separator)
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `energiegemeinschaft-${gemeindeName.toLowerCase().replace(/\s/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Excel export:', error);
      alert('Fehler beim Erstellen des Excel-Exports');
    } finally {
      setIsExporting(null);
    }
  };

  const handleInfographic = async () => {
    setIsExporting('infographic');
    analytics.trackPdfExport(gemeindeName, 'infographic');
    try {
      await generateInfographic(gemeindeName, data);
      analytics.track('infographic_export_success', { 
        municipality: gemeindeName 
      });
    } catch (error) {
      console.error('Error generating infographic:', error);
      analytics.trackError(error as Error, 'infographic_export');
      alert('Fehler beim Erstellen der Infografik');
    } finally {
      setIsExporting(null);
    }
  };

  const handleEmailShare = () => {
    analytics.trackButtonClick('email_share', 'export_section', { municipality: gemeindeName });
    const subject = `Energiegemeinschaft ${gemeindeName} - Potenzialanalyse`;
    const body = `Sehr geehrte Damen und Herren,

anbei finden Sie die Potenzialanalyse für eine Energiegemeinschaft in ${gemeindeName}:

Jährliches Potenzial: €${data.jahresPotenzial.toLocaleString()}
Einwohner: ${data.einwohner.toLocaleString()}
Haushalte: ${data.haushalte.toLocaleString()}

Mit freundlichen Grüßen`;

    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Card className="p-6 bg-gradient-to-br from-gray-50 to-white">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">
          Ihre Daten mitnehmen
        </h3>
        <Download className="h-5 w-5 text-gray-400" />
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* PDF Export */}
        <div className="relative group">
          <button
            onClick={handlePDFExport}
            disabled={isExporting !== null}
            className="w-full p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-red-400 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center space-x-3">
              {isExporting === 'pdf' ? (
                <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
              ) : (
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <FileText className="h-6 w-6 text-red-600" />
                </div>
              )}
              <div className="text-left">
                <p className="font-medium text-gray-900">PDF-Bericht erstellen</p>
                <p className="text-xs text-gray-500">Detaillierter Analysebericht</p>
              </div>
            </div>
          </button>
        </div>

        {/* Excel Export */}
        <div className="relative group">
          <button
            onClick={handleExcelExport}
            disabled={isExporting !== null}
            className="w-full p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-green-400 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center space-x-3">
              {isExporting === 'excel' ? (
                <Loader2 className="h-8 w-8 text-green-500 animate-spin" />
              ) : (
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center group-hover:bg-green-100 transition-colors">
                  <FileSpreadsheet className="h-6 w-6 text-green-600" />
                </div>
              )}
              <div className="text-left">
                <p className="font-medium text-gray-900">Excel-Export</p>
                <p className="text-xs text-gray-500">CSV-Datei für Tabellenkalkulation</p>
              </div>
            </div>
          </button>
        </div>

        {/* Infographic */}
        <div className="relative group">
          <button
            onClick={handleInfographic}
            disabled={isExporting !== null}
            className="w-full p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center space-x-3">
              {isExporting === 'infographic' ? (
                <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
              ) : (
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                  <Presentation className="h-6 w-6 text-purple-600" />
                </div>
              )}
              <div className="text-left">
                <p className="font-medium text-gray-900">Infografik für Gemeinderat</p>
                <p className="text-xs text-gray-500">Visuelle Zusammenfassung</p>
              </div>
            </div>
          </button>
        </div>

        {/* Email Share */}
        <div className="relative group">
          <button
            onClick={handleEmailShare}
            disabled={isExporting !== null}
            className="w-full p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <Mail className="h-6 w-6 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Per E-Mail teilen</p>
                <p className="text-xs text-gray-500">Link zur Analyse versenden</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      <div className="mt-6 p-3 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600 text-center">
          ✓ Alle Exporte enthalten die vollständigen Daten Ihrer Potenzialanalyse
        </p>
      </div>
    </Card>
  );
}